import React, { useState, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';

import { Button, Text, H2, Panel, Table, Small, Box, Input, Select, Modal, Flex, FlexItem } from '@bigcommerce/big-design';
import { FileDownloadIcon, SearchIcon } from '@bigcommerce/big-design-icons';

import ToolPage from '../partials/ToolPage';
import LoadingSpinner from '../partials/LoadingSpinner';
import PrintOptionsModal from './PrintOptionsModal';

import { warehouseAreasIndex, warehouseAreasBreakdown, warehouseAreasForget } from '../../redux/actions/warehouseAreas';

const WarehouseLabels = ({ payload, warehouseAreas, loadAreas, startBreakdown, forgetAreas }) => {
  const [searchText, setSearchText] = useState('');
  const [depthFilter, setDepthFilter] = useState(null);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [depthOptions, setDepthOptions] = useState([]);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showForgetModal, setShowForgetModal] = useState(false);
  const searchTimeout = useRef(null);

  const meta = warehouseAreas.meta || {};
  const running = meta.status === 'running';

  const rows = useMemo(
    () => warehouseAreas.areas.map(item => ({ area: item.area, depth: item.depth })),
    [warehouseAreas.areas]
  );

  const load = (search, depth) => {
    loadAreas(payload, { search: search, depth: depth });
  }

  useEffect(() => {
    load(searchText, depthFilter);
  }, []);

  useEffect(() => {
    if (!running) {
      return;
    }

    const timer = setInterval(() => load(searchText, depthFilter), 3000);

    return () => clearInterval(timer);
  }, [running, searchText, depthFilter]);

  useEffect(() => {
    setDepthOptions((previous) => {
      const found = new Set(previous);
      warehouseAreas.areas.forEach((item) => found.add(item.depth));

      return Array.from(found).sort((a, b) => a - b);
    });
  }, [warehouseAreas.areas]);

  const onSearchChange = (event) => {
    const value = event.target.value;
    setSearchText(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => load(value, depthFilter), 400);
  }

  const onDepthChange = (value) => {
    const depth = value === 'all' ? null : value;
    setDepthFilter(depth);
    load(searchText, depth);
  }

  const selectedRows = () => rows.filter((row) => selectedAreas.includes(row.area));

  const confirmForget = () => {
    setSelectedAreas([]);
    forgetAreas(payload);
    setShowForgetModal(false);
  }

  const description = () => {
    return(
      <>
        <Text>Print warehouse design labels based on the areas implied by your product and variant Bin Picking Numbers (BPNs). Areas are broken down using the "{warehouseAreas.bpnSeparator}" separator from the Pack Perfect app.</Text>
        <Text>Click "Break Down All Item BPNs" to scan your catalog and rebuild the known warehouse areas, then select areas below to print their labels.</Text>
      </>
    )
  }

  return(
    <>
      <ToolPage title="Warehouse Design Labels" description={description()} helpLink="bigcommerce/">
        <Panel>
          <Flex justifyContent="space-between">
            <FlexItem>
              <Button
                onClick={() => startBreakdown(payload)}
                isLoading={running}
                disabled={running}
              >
                Break Down All Item BPNs
              </Button>
            </FlexItem>
            <FlexItem>
              <Button
                variant="secondary"
                onClick={() => setShowForgetModal(true)}
                disabled={running || warehouseAreas.areas.length === 0}
              >
                Forget All Known Areas
              </Button>
            </FlexItem>
          </Flex>

          { running &&
            <LoadingSpinner size="xxSmall" statusText="Breaking down BPNs - this can take a while for large catalogs" />
          }
          { meta.status === 'error' &&
            <Box
              backgroundColor="danger10"
              border="box"
              borderRadius="normal"
              padding="medium"
            >
              <Text>The last breakdown attempt failed{ meta.error ? `: ${meta.error}` : '.' } Try again.</Text>
            </Box>
          }
          { meta.status === 'complete' && meta.finished_at &&
            <Small>Last breakdown: {meta.finished_at} ({meta.area_count} areas found)</Small>
          }
        </Panel>

        <Panel>
          <H2>Known Areas</H2>

          <Flex justifyContent="space-between" alignItems="flex-end" marginBottom="medium">
            <FlexItem flexGrow={1}>
              <Input
                iconLeft={<SearchIcon />}
                placeholder="Search areas"
                value={searchText}
                onChange={onSearchChange}
              />
            </FlexItem>
            <FlexItem marginLeft="medium">
              <Select
                label="Depth"
                required
                onOptionChange={onDepthChange}
                options={[
                  { value: 'all', content: 'All depths' },
                  ...depthOptions.map((depth) => ({ value: depth, content: `Depth ${depth}` }))
                ]}
                value={depthFilter === null ? 'all' : depthFilter}
              />
            </FlexItem>
          </Flex>

          { warehouseAreas.status !== 'success' && warehouseAreas.isFetching &&
            <LoadingSpinner statusText="Loading warehouse areas" />
          }

          { warehouseAreas.status === 'success' &&
            <>
              { rows.length === 0 &&
                <Box
                  backgroundColor="warning10"
                  border="box"
                  borderRadius="normal"
                  padding="medium"
                >
                  <div className="text-center">
                    <Text>No known warehouse areas{ searchText || depthFilter ? ' match your filters' : '' }. Run the BPN breakdown to build the list.</Text>
                  </div>
                </Box>
              }

              { rows.length > 0 &&
                <>
                  <Table
                    columns={[
                      { hash: 'area', header: 'Area', render: ({ area }) => area },
                      { hash: 'depth', header: 'Depth', render: ({ depth }) => depth },
                    ]}
                    itemName="Areas"
                    items={rows}
                    keyField="area"
                    selectable={{
                      selectedItems: selectedRows(),
                      onSelectionChange: (items) => setSelectedAreas(items.map((item) => item.area))
                    }}
                    stickyHeader
                  />

                  <Flex marginTop="medium" justifyContent="flex-end">
                    <FlexItem>
                      <Button
                        iconLeft={<FileDownloadIcon />}
                        disabled={selectedAreas.length === 0}
                        onClick={() => setShowPrintModal(true)}
                      >
                        Print {selectedAreas.length > 0 ? `${selectedAreas.length} ` : ''}Labels
                      </Button>
                    </FlexItem>
                  </Flex>
                </>
              }
            </>
          }
        </Panel>
      </ToolPage>

      { showPrintModal &&
        <PrintOptionsModal
          payload={payload}
          areas={selectedAreas}
          isOpen={showPrintModal}
          onClose={() => setShowPrintModal(false)}
        />
      }

      { showForgetModal &&
        <Modal
          actions={[
            {
              text: 'Cancel',
              variant: 'subtle',
              onClick: () => setShowForgetModal(false),
            },
            {
              text: 'Forget Areas',
              onClick: () => confirmForget()
            },
          ]}
          closeOnClickOutside={true}
          closeOnEscKey={true}
          header="Forget All Known Areas"
          isOpen={showForgetModal}
          onClose={() => setShowForgetModal(false)}
        >
          <Text>
            This deletes every known warehouse area for this store. Run the BPN breakdown again to rebuild the list.
          </Text>
        </Modal>
      }
    </>
  )
}

const mapStateToProps = state => ({
  warehouseAreas: state.warehouseAreas,
});

const mapDispatchToProps = (dispatch) => ({
  loadAreas: (payload, filters) => dispatch(warehouseAreasIndex(payload, filters)),
  startBreakdown: (payload) => dispatch(warehouseAreasBreakdown(payload)),
  forgetAreas: (payload) => dispatch(warehouseAreasForget(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WarehouseLabels);
